import { AlertTriangle } from "lucide-react";
import { useTranslation } from "react-i18next";
import GenericCrudModule from "@/components/GenericCrudModule";
import ContextButton from "@/components/ContextButton";
import ThreeQPageHeader from "@/components/ThreeQPageHeader";

const GRAVITE_COLORS = { mineur: "#A1A1AA", majeur: "#EF9F27", critique: "#E24B4A" };
const STATUT_COLORS = { ouvert: "#E24B4A", en_cours: "#EF9F27", clos: "#1D9E75" };

export default function Incidents() {
  const { t } = useTranslation();
  const today = new Date().toISOString().slice(0, 10);
  return (
    <>
      <ThreeQPageHeader endpoint="/workspace/incidents/top-priority" className="mb-4" />
      <GenericCrudModule
      module={{
        path: "incidents",
        title: t("saas.incidents.title"),
        overline: t("saas.incidents.overline"),
        subtitle: t("saas.incidents.subtitle"),
        accent: "#E24B4A", icon: AlertTriangle, dataTestId: "incidents",
      }}
      fields={[
        { key: "titre", label: t("saas.incidents.field_titre"), required: true, span: 2,
          placeholder: t("saas.incidents.field_titre_ph") },
        { key: "date_incident", label: t("saas.incidents.field_date"), type: "date", required: true, default: today },
        { key: "gravite", label: t("saas.incidents.field_gravite"), type: "select", default: "majeur",
          options: ["mineur", "majeur", "critique"].map((v) => ({ value: v, label: t(`saas.incidents.gravites.${v}`) })) },
        { key: "statut", label: t("saas.incidents.field_statut"), type: "select", default: "ouvert",
          options: ["ouvert", "en_cours", "clos"].map((v) => ({ value: v, label: t(`saas.incidents.statuts.${v}`) })) },
        { key: "impact_keur", label: t("saas.incidents.field_impact"), type: "number", default: 0 },
        { key: "description", label: t("saas.incidents.field_description"), type: "textarea", span: 2 },
      ]}
      columns={[
        { key: "titre", label: t("saas.incidents.col_incident") },
        { key: "date_incident", label: t("saas.incidents.col_date") },
        { key: "gravite", label: t("saas.incidents.col_gravite"), render: (r) => (
          <span className="text-[10px] px-2 py-0.5 rounded-full font-medium"
            style={{ background: `${GRAVITE_COLORS[r.gravite]}1A`, color: GRAVITE_COLORS[r.gravite] }}>
            {t(`saas.incidents.gravites.${r.gravite}`, { defaultValue: r.gravite })}
          </span>
        )},
        { key: "impact_keur", label: t("saas.incidents.col_impact"), render: (r) => r.impact_keur > 0 ? `${r.impact_keur} k€` : "—" },
        { key: "statut", label: t("saas.incidents.col_statut"), render: (r) => (
          <span className="text-[10px] px-2 py-0.5 rounded-full font-medium uppercase tracking-wider"
            style={{ background: `${STATUT_COLORS[r.statut]}1A`, color: STATUT_COLORS[r.statut] }}>
            {t(`saas.incidents.statuts.${r.statut}`, { defaultValue: r.statut })}
          </span>
        )},
      ]}
      emptyMsg={t("saas.incidents.empty_msg")}
      primaryCta={t("saas.incidents.primary_cta")}
      headerActions={<ContextButton moduleKey="incidents" />}
      kpis={(data) => {
        const items = data.items || [];
        const total = items.length;
        const ouverts = items.filter((i) => i.statut === "ouvert" || i.statut === "en_cours").length;
        const critiques = items.filter((i) => i.gravite === "critique").length;
        const impact = items.reduce((s, i) => s + (i.impact_keur || 0), 0);
        return [
          { label: t("saas.incidents.kpi_total"), value: total },
          { label: t("saas.incidents.kpi_open"), value: ouverts, color: "#EF9F27" },
          { label: t("saas.incidents.kpi_critical"), value: critiques, color: "#E24B4A", hint: t("saas.incidents.kpi_critical_hint") },
          { label: t("saas.incidents.kpi_impact"), value: `${impact} k€`, color: "#378ADD" },
        ];
      }}
    />
    </>
  );
}
